import { ScreenHeader } from "@/components/common/ScreenHeader";
import { StripeConnectEmbed } from "@/components/StripeConnectEmbed";
import { ThemedText } from "@/components/themed-text";
import { BrandColors } from "@/constants/theme";
import { useAuth } from "@/contexts/auth-context";
import { showAlert } from "@/lib/alert";
import { stripeConnectService } from "@/lib/services/stripe-connect";
import Ionicons from "@react-native-vector-icons/ionicons";
import { router } from "expo-router";
import React, { useState } from "react";
import {
  ActivityIndicator,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

export default function WalletScreen() {
  const { user, theme } = useAuth();
  const [stripeLoading, setStripeLoading] = useState(false);

  const handleConnectStripe = async () => {
    setStripeLoading(true);
    try {
      // First create/get account
      await stripeConnectService.createAccount();
      // Then get onboarding link
      const { url } = await stripeConnectService.getOnboardingLink();
      // Open in browser
      router.push({
        pathname: "/stripe-onboarding",
        params: {
          url,
        },
      });
    } catch (error: any) {
      showAlert(
        "Error",
        error.response?.data?.message || "Failed to connect with Stripe",
      );
    } finally {
      setStripeLoading(false);
    }
  };

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: theme.background }]}
      edges={["top"]}
    >
      <ScreenHeader title="Wallet" />
      {user?.role === "driver" && (
        <View style={[styles.stripeSection, { flex: 1 }]}>
          {user.stripe_account?.payouts_enabled ? (
            <StripeConnectEmbed />
          ) : (
            <View style={styles.stripeCard}>
              <View style={styles.stripeHeader}>
                <Ionicons name="card" size={24} color={BrandColors.primary} />
                <ThemedText style={styles.stripeTitle} type="defaultSemiBold">
                  Stripe Payouts
                </ThemedText>
              </View>
              <View style={styles.stripeStatus}>
                <ThemedText style={styles.stripeInfo} type="small">
                  Connect your Stripe account to receive automatic weekly
                  payouts.
                </ThemedText>
                <TouchableOpacity
                  style={styles.connectButton}
                  onPress={handleConnectStripe}
                  disabled={stripeLoading}
                >
                  {stripeLoading ? (
                    <ActivityIndicator color="#fff" size="small" />
                  ) : (
                    <>
                      <Ionicons name="card-outline" size={20} color="#fff" />
                      <ThemedText
                        style={styles.connectButtonText}
                        type="defaultSemiBold"
                      >
                        {user.stripe_account?.details_submitted
                          ? "Complete Setup"
                          : "Connect Stripe"}
                      </ThemedText>
                    </>
                  )}
                </TouchableOpacity>
              </View>
            </View>
          )}
        </View>
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: BrandColors.backgroundGray,
  },
  centerContent: {
    justifyContent: "center",
    alignItems: "center",
  },
  stripeSection: {
    paddingHorizontal: 20,
    marginBottom: 20,
  },
  stripeCard: {
    backgroundColor: "#fff",
    borderRadius: 20,
    padding: 20,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.05,
    shadowRadius: 10,
    elevation: 3,
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.03)",
  },
  stripeHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 16,
    gap: 12,
  },
  stripeTitle: {
    fontSize: 18,
    fontWeight: "700",
    color: BrandColors.charcoal,
  },
  stripeStatus: {
    gap: 12,
  },
  statusBadge: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: BrandColors.success + "15",
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
    alignSelf: "flex-start",
    gap: 6,
  },
  statusText: {
    color: BrandColors.success,
    fontSize: 14,
    fontWeight: "600",
  },
  stripeInfo: {
    fontSize: 14,
    color: BrandColors.mediumGray,
    lineHeight: 20,
  },
  connectButton: {
    backgroundColor: BrandColors.primary,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 14,
    borderRadius: 12,
    gap: 8,
    marginTop: 4,
  },
  connectButtonText: {
    color: "#fff",
    fontSize: 16,
    fontWeight: "600",
  },
});
